import { Controller } from "@hotwired/stimulus"

// Manages bulk selection and actions for product lists
export default class extends Controller {
  static targets = ["checkbox", "selectAll", "actionBar", "selectedCount"]

  connect() {
    this.updateUI()
  }

  toggle() {
    this.updateUI()
  }

  toggleAll() {
    const checked = this.selectAllTarget.checked
    this.checkboxTargets.forEach(cb => cb.checked = checked)
    this.updateUI()
  }

  updateUI() {
    const selected = this.selectedIds
    const count = selected.length

    // Update count display
    this.selectedCountTarget.textContent = count

    // Show/hide action bar
    this.actionBarTarget.classList.toggle("hidden", count === 0)

    // Update select all state
    if (this.checkboxTargets.length > 0) {
      this.selectAllTarget.checked = count > 0 && count === this.checkboxTargets.length
      this.selectAllTarget.indeterminate = count > 0 && count < this.checkboxTargets.length
    }
  }

  get selectedIds() {
    return this.checkboxTargets
      .filter(cb => cb.checked)
      .map(cb => cb.value)
  }

  static values = { url: { type: String, default: "/dashboard/products/bulk_action" } }

  // Product actions
  publish() {
    this.submitAction("publish")
  }

  unpublish() {
    this.submitAction("unpublish")
  }

  recalculate() {
    const count = this.selectedIds.length
    if (confirm(`Recalculate prices for ${count} product${count !== 1 ? 's' : ''}? This will update prices based on current market data.`)) {
      this.submitAction("recalculate")
    }
  }

  delete() {
    const count = this.selectedIds.length
    if (confirm(`Delete ${count} product${count !== 1 ? 's' : ''}? This cannot be undone.`)) {
      this.submitAction("delete")
    }
  }

  // Order actions
  markShipped() {
    const count = this.selectedIds.length
    if (confirm(`Mark ${count} order${count !== 1 ? 's' : ''} as shipped? Customers will be notified.`)) {
      this.submitAction("mark_shipped")
    }
  }

  markDelivered() {
    this.submitAction("mark_delivered")
  }

  printPackingSlips() {
    this.submitFormTo("/dashboard/orders/bulk_packing_slips", "_blank")
  }

  // Modal actions for price/quantity
  openPriceModal() {
    const modal = document.getElementById("bulk-price-modal")
    if (modal) {
      modal.classList.remove("hidden")
      this.updateModalIds("bulk-price-ids")
    }
  }

  openQuantityModal() {
    const modal = document.getElementById("bulk-quantity-modal")
    if (modal) {
      modal.classList.remove("hidden")
      this.updateModalIds("bulk-quantity-ids")
    }
  }

  updateModalIds(inputId) {
    const input = document.getElementById(inputId)
    if (input) {
      input.value = this.selectedIds.join(",")
    }
  }

  submitAction(action) {
    this.submitFormTo(this.urlValue, "_self", action)
  }

  submitFormTo(url, target, action) {
    const form = document.createElement("form")
    form.method = "POST"
    form.action = url
    if (target) form.target = target

    // CSRF token
    const csrfToken = document.querySelector("meta[name='csrf-token']")?.content
    if (csrfToken) {
      const csrfInput = document.createElement("input")
      csrfInput.type = "hidden"
      csrfInput.name = "authenticity_token"
      csrfInput.value = csrfToken
      form.appendChild(csrfInput)
    }

    // Action (if provided)
    if (action) {
      const actionInput = document.createElement("input")
      actionInput.type = "hidden"
      actionInput.name = "bulk_action"
      actionInput.value = action
      form.appendChild(actionInput)
    }

    // IDs
    this.selectedIds.forEach(id => {
      const idInput = document.createElement("input")
      idInput.type = "hidden"
      idInput.name = "ids[]"
      idInput.value = id
      form.appendChild(idInput)
    })

    document.body.appendChild(form)
    form.submit()
  }
}
